import type { CSSProperties, ReactNode, Ref } from "react";
import { SOFT } from "./soft-tokens";

/**
 * The chat header's controls, as one object.
 *
 * They used to be two or three separate outlined pills in a row — map width,
 * new chat, history — each carrying its own ring and each the same weight as
 * the next, so they competed with one another and with the title beside them.
 * Joined into a single capsule they read as one toolbar: one ring around the
 * set, hairlines between the segments.
 *
 * **The capsule is a control, not a panel.** It wears white with one navy
 * hairline, not the pewter band — buttons, chips and fields never take the
 * band (owner ruling, 2026-09-05, recorded in `discover.css`). The band
 * belongs to the panel the toolbar sits in.
 *
 * Shared between the modal and the Discover panel, per the ai-chat-parity
 * rule: the modal has two segments, Discover has three.
 */
export function ChatToolbar({ children }: { children: ReactNode }) {
  return (
    <>
      <div
        className="mj-chat-toolbar"
        style={{
          display: "inline-flex",
          alignItems: "stretch",
          height: TOOLBAR_HEIGHT,
          borderRadius: 999,
          // Lit, not flat: a faint top-to-bottom fall so the capsule reads as
          // a surface with light on it. This is a FILL gradient, not the band
          // — the 2026-09-05 ruling that a control never wears the band is
          // about the gradient border, and the ring below is still the plain
          // navy hairline. One gradient across the whole capsule rather than
          // one per segment, which would band at every divider.
          background: SOFT.controlFill,
          boxShadow: `0 0 0 1.5px ${SOFT.controlEdge}`,
          // NOT `overflow: hidden`. The history menu's panel renders inside
          // this capsule, and a capsule 30px tall that clips its overflow
          // deletes the panel — the chevron flips, aria-expanded goes true,
          // and nothing appears. The hover fills are kept inside the rounded
          // ends by rounding the end segments themselves (below).
        }}
      >
        {children}
      </div>
      {/* Outside the capsule: a <style> node inside it is a flex child, and a
          `:first-child` / `+` selector then counts it. */}
      <style>{`
        /* Descendant, not child: the history segment's trigger is wrapped in
           the action menu's own div, so a "> button" rule skipped it — it lost
           its padding, its divider and its height, and the unstyled wrapper
           stretched the capsule to 45px. */
        .mj-chat-toolbar button {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          height: 100%;
          padding: 0 11px;
          border: 0;
          border-inline-start: 1px solid ${SOFT.hairline};
          background: transparent;
          cursor: pointer;
          font-size: 11.5px;
          font-weight: 600;
          color: ${SOFT.ink};
          white-space: nowrap;
          transition: background 0.16s ease;
        }
        /* Every segment carries the divider on its leading edge; the first one
           gives it back, whether it is a bare button or a wrapped one. */
        .mj-chat-toolbar > *:first-child,
        .mj-chat-toolbar > *:first-child button {
          border-inline-start: 0;
        }
        /* A wrapper around a segment must not become a segment itself. It
           is also the menu's positioning context, so it stays visible. */
        .mj-chat-toolbar > div {
          display: inline-flex;
          align-items: stretch;
          position: relative;
        }
        /* The capsule's rounded ends, carried by the segments that sit at
           them — so a hover fill stops at the curve without the capsule
           having to clip anything away. */
        .mj-chat-toolbar > *:first-child,
        .mj-chat-toolbar > *:first-child button {
          border-start-start-radius: 999px;
          border-end-start-radius: 999px;
        }
        .mj-chat-toolbar > *:last-child,
        .mj-chat-toolbar > *:last-child button {
          border-start-end-radius: 999px;
          border-end-end-radius: 999px;
        }
        .mj-chat-toolbar button:hover,
        .mj-chat-toolbar button[aria-expanded="true"] {
          background: rgba(17, 14, 57, 0.055);
        }
        .mj-chat-toolbar button:focus-visible {
          outline: 2px solid ${SOFT.inkWell};
          outline-offset: -2px;
        }
      `}</style>
    </>
  );
}

/** Clears the 24×24 hit-area floor (WCAG 2.5.8) with the label's line box. */
const TOOLBAR_HEIGHT = 30;


/**
 * One segment. Deliberately styleless in JS — the capsule's stylesheet drives
 * every segment, so a new control cannot arrive wearing its own ring again.
 *
 * `rest` is spread last on purpose: a menu trigger's handler and aria arrive
 * that way, and anything written after the spread would overwrite them.
 */
export function ChatToolbarButton({
  onClick,
  label,
  icon,
  trailing,
  buttonRef,
  rest,
  style,
}: {
  onClick?: () => void;
  label: string;
  icon?: ReactNode;
  /** A count, a chevron — whatever sits after the word. */
  trailing?: ReactNode;
  buttonRef?: Ref<HTMLButtonElement>;
  /** Menu trigger props (aria-expanded, aria-controls, keyboard handlers). */
  rest?: Record<string, unknown>;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      ref={buttonRef}
      title={label}
      className="mj-press"
      style={style}
      // Only when there is one. Writing `onClick={onClick}` unconditionally
      // set it to undefined for the menu trigger — which has no onClick of
      // its own and carries `dd.toggle` inside `rest` — and killed the
      // History dropdown outright.
      {...(onClick ? { onClick } : {})}
      // Spread LAST so a trigger's own handler and aria always win.
      {...rest}
    >
      {icon}
      <span>{label}</span>
      {trailing}
    </button>
  );
}
